<template>
  <v-app id="app" style="overflow-x: clip;">
    <v-app-bar
      v-if="route.name !== 'Login' && !hideChrome"
      app
      color="white"
      flat
      height="64"
    >
      <div class="d-flex align-items-center justify-space-between w-100">
        <div class="d-flex align-center">
          <!-- History back -->
          <v-btn
            v-if="canNavigateBack && !backLink"
            class="ms-n2 me-2"
            icon
            size="24"
            @click="goBack"
          >
            <icon height="12" width="12">angle-left</icon>
          </v-btn>

          <!-- Explicit back link -->
          <v-btn
            v-if="backLink"
            class="ms-n2 me-2"
            icon
            size="24"
            :to="backLink"
          >
            <icon height="12" width="12">angle-left</icon>
          </v-btn>

          <nav-logo
            v-if="offer.isPreview"
            :loading="logoLoading"
            :logo="logo"
          />
          <nav-logo
            v-else
            :loading="logoLoading"
            :logo="logo"
            :to="{ path: '/experiences', query: { t: token } }"
          />

                   
        </div>

        <div class="d-flex flex-shrink-1 align-center justify-end mr-n2 gap-1">
          <div
            v-if="showProgressHeaderChip"
            class="progress-header-chip"
            :class="{ 'progress-header-chip--tight': isDev || isBandwangoOrder }"
          >
            <progress-header
              variant="bar"
              @open-modal="progressDialogOpen = true"
            />
          </div>
          <icon-label-btn
            v-if="(isDev || isBandwangoOrder)"
            color="red"
            size="48"
            @click="clearStorage"
          >
            trash
          </icon-label-btn>
        </div>
      </div>
    </v-app-bar>

    <add-to-homescreen
      v-if="SHOW_A2HS_FOR_TESTING || (!offer.isPreview && isLoggedIn && partnerUser.id)"
    />

    <v-main :style="hideChrome ? 'padding-bottom: 0 !important;' : 'padding-bottom: 90px !important;'">
      <v-progress-linear
        v-if="loading"
        color="primary-lighten-2"
        height="2"
        indeterminate
      />
      <router-view v-slot="{ Component }">
        <transition name="fade">
          <component :is="Component" />
        </transition>
      </router-view>
    </v-main>

    <Footer v-if="route.name !== 'Login' && initialFetch && !hideChrome" />

    <v-dialog
      v-model="progressDialogOpen"
      content-class="show-modal-popup"
      persistent
      scrim="rgba(0, 0, 0, 0.65)"
      opacity="0.65"
    >
      <v-card class="show-modal-popup show-modal-popup__card dialog-card">
        <v-card-text class="pa-0">
          <progress-expanded
            :show-collapse="false"
            embedded
            show-dialog-close
            dialog-scrollable
            @close="progressDialogOpen = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- iOS Safari fallback: re-enable if Intercom X remains untappable (see intercom.js listeners). -->
    <!--
    <Teleport to="body">
      <div
        v-if="intercomCloseFallbackVisible"
        class="bw-intercom-close-fallback"
        role="region"
        aria-label="Support chat"
      >
        <button
          class="bw-intercom-close-fallback__btn"
          type="button"
          @click="closeIntercomMessenger"
        >
          Close support chat
        </button>
      </div>
    </Teleport>
    -->
  </v-app>
</template>

<script setup>
/* Set to true to show Add to Homescreen modal by default (for testing). */
const SHOW_A2HS_FOR_TESTING = false;

/* -------------------------------------------------
   Imports
------------------------------------------------- */
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useTheme } from 'vuetify';

import AddToHomescreen from '@/components/shared/AddToHomescreen/index.vue';
import Footer from '@/components/Footer/index.vue';
import NavLogo from '@/components/shared/NavLogo/index.vue';
import IconLabelBtn from '@/components/shared/IconLabelBtn/index.vue';
import ProgressHeader from '@/components/progress/ProgressHeader.vue';
import ProgressExpanded from '@/components/progress/ProgressExpanded.vue';

import axios from './api/setupAxios';
import $api from './api';
import themes from './themes';
import applyPartnerFont from './partnerFonts';
import { clearVuexStorage, gtmPageView, pascalCase } from './helpers';

/* -------------------------------------------------
   Core hooks
------------------------------------------------- */
const store = useStore();
const route = useRoute();
const router = useRouter();
const theme = useTheme();

/* -------------------------------------------------
   Local state
------------------------------------------------- */
const loading = ref(true);
const logoLoading = ref(true);
const initialFetch = ref(false);
const lastRouteName = ref('');
const progressDialogOpen = ref(false);

/* Close support chat fallback (iOS Safari) — paired with Teleport + intercom.js onShow/onHide; */
// const intercomCloseFallbackVisible = ref(false);
// const onIntercomMessengerShow = () => {
//   intercomCloseFallbackVisible.value = true;
// };
// const onIntercomMessengerHide = () => {
//   intercomCloseFallbackVisible.value = false;
// };
// function closeIntercomMessenger() {
//   if (typeof window.Intercom === 'function') {
//     window.Intercom('hide');
//   }
//   intercomCloseFallbackVisible.value = false;
// }

const NESTED_EXPERIENCE_ROUTE_NAMES = [
  'Product',
  'Venue',
  'VenueProduct',
  'Deal',
  'DealProduct',
  'DealVenue',
  'DealVenueProduct'
];

watch(
  () => store.state.initialData?.ready,
  (ready) => {
    if (ready) {
      logoLoading.value = false;
      setAppTheme();
      setAppFont();
    }
  },
  { immediate: true }
);

/* -------------------------------------------------
   Vuex state
------------------------------------------------- */
const offer = computed(() => store.state.offer);
const partner = computed(() => store.state.partner);
const partnerUser = computed(() => store.state.partnerUser);
const isLoggedIn = computed(() => store.state.auth.validated);

/* -------------------------------------------------
   Vuex getters
------------------------------------------------- */
const findProduct = (id, dealId) =>
  store.getters['products/find'](id, dealId);

/* -------------------------------------------------
   Computed
------------------------------------------------- */
const token = computed(() => window.token);

const isDev = computed(() => import.meta.env.MODE !== 'production');

const isBandwangoOrder = computed(() =>
  partnerUser.value?.email
    ? /@bandwango.com\s*$/.test(partnerUser.value.email)
    : false
);

const isHome = computed(() => {
  const { path } = router.resolve({ name: 'Experiences' });
  return route.path === path;
});

// Custom full-screen pass layouts (e.g. Daily Giveaway) render their own chrome
// and must suppress the app header, footer tab bar, and reserved bottom padding.
const hideChrome = computed(
  () => route.name === 'Deal' && store.getters['giveaway/hasGiveaway']
);

const showProgressHeaderChip = computed(
  () =>
    route.name !== 'Login' &&
    store.getters['progressTracker/isProgressTrackerVisible']
);

const hasOfferTheme = computed(
  () => offer.value?.styles?.colors?.primary?.main
)

const hasPartnerTheme = computed(
  () => partner.value?.styles?.colors?.primary?.main
)

/* -------------------------------------------------
   Back link
------------------------------------------------- */
const backLink = computed(() => {
  if (!offer.value.isPreview) return false;

  if (
    route.name?.endsWith('Product') &&
    offer.value.previewType === 'product'
  ) {
    const product = findProduct(Number(route.params.productId));
    const venueId = product?.venue_id ?? null;
    if (!venueId) return false;

    const dealPath = offer.value.previewDealId
      ? `/deal/${offer.value.previewDealId}`
      : '';

    return {
      path: `/experiences${dealPath}/venue/${venueId}`,
      query: { t: route.query.t }
    };
  }

  return false;
});

const canNavigateBack = computed(() => {
  if (isHome.value) return false;

  if (!offer.value.isPreview) return true;

  if (
    lastRouteName.value &&
    NESTED_EXPERIENCE_ROUTE_NAMES.includes(lastRouteName.value)
  ) {
    return true;
  }

  // Deep link / refresh: no previous in-app route name, but we can still go to parent
  if (NESTED_EXPERIENCE_ROUTE_NAMES.includes(route.name)) {
    return true;
  }

  return false;
});

const logo = computed(() => {
  const offerLogo = offer.value?.media?.find(
    m => m.location === 'HorizontalLogo' && m.url
  );
  const partnerLogo = partner.value?.media?.find(
    m => m.location === 'HorizontalLogo' && m.url
  );
  return offerLogo || partnerLogo;
});

/* -------------------------------------------------
   Methods
------------------------------------------------- */
function getFallbackBackRoute() {
  const query = route.query ?? {};
  const name = route.name;
  const params = route.params;

  switch (name) {
    case 'DealVenueProduct':
      return {
        name: 'DealVenue',
        params: { dealId: params.dealId, venueId: params.venueId },
        query
      };
    case 'DealProduct':
      return {
        name: 'Deal',
        params: { dealId: params.dealId },
        query
      };
    case 'VenueProduct':
      return {
        name: 'Venue',
        params: { venueId: params.venueId },
        query
      };
    case 'Product':
      return { name: 'Experiences', query };
    case 'DealVenue':
      return {
        name: 'Deal',
        params: { dealId: params.dealId },
        query
      };
    case 'Venue':
      return { name: 'Experiences', query };
    case 'Deal':
      return { name: 'Experiences', query };
    default: {
      const parent = route.meta?.parent;
      if (parent === 'Experiences') return { name: 'Experiences', query };
      if (parent === 'More') return { name: 'More', query };
      if (parent === 'Cart') return { name: 'Cart', query };
      if (parent === 'Rewards') return { name: 'Rewards', query };
      return { name: 'Experiences', query };
    }
  }
}

function goBack() {
  if (NESTED_EXPERIENCE_ROUTE_NAMES.includes(route.name)) {
    // Keep nested experience navigation deterministic and avoid history loops
    router.replace(getFallbackBackRoute());
    return;
  }

  if (lastRouteName.value && lastRouteName.value !== 'Login') {
    router.back();
    return;
  }
  router.replace(getFallbackBackRoute());
}

const clearStorage = () => {
  clearVuexStorage();
  window.location.reload();
};

function setAppFont() {
  const family = applyPartnerFont(partner.value?.styles);
  const themesRef = theme.themes;
  if (!themesRef?.value?.light) return;

  themesRef.value = {
    ...themesRef.value,
    light: {
      ...themesRef.value.light,
      variables: {
        ...(themesRef.value.light.variables || {}),
        'body-font-family': family,
      },
    },
  };
}

function setAppTheme() {
  const primary =
    offer.value?.styles?.colors?.primary?.main ||
    partner.value?.styles?.colors?.primary?.main ||
    themes.ui.light.colors.primary;

  const onPrimary =
    offer.value?.styles?.colors?.primary?.backgroundText ||
    partner.value?.styles?.colors?.primary?.backgroundText;

  const themesRef = theme.themes;
  if (!themesRef?.value?.light) return;

  themesRef.value = {
    ...themesRef.value,
    light: {
      ...themesRef.value.light,
      colors: {
        ...themesRef.value.light.colors,
        primary,
        ...(onPrimary && { onPrimary }),
      },
    },
  };
}

/* -------------------------------------------------
   Lifecycle
------------------------------------------------- */
onMounted(async () => {
  // window.addEventListener('bw-intercom-show', onIntercomMessengerShow);
  // window.addEventListener('bw-intercom-hide', onIntercomMessengerHide);
  // const urlParams = new URLSearchParams(window.location.search);
  // window.token = urlParams.get('t');

  // axios.interceptors.request.use(config => {
  //   config.headers['BW-token'] = window.token;
  //   config.headers['BW-Api-Version'] = $api.version;
  //   config.params = { ...config.params, t: window.token };

  //   if (urlParams.get('dealId')) {
  //     config.params.dealId = urlParams.get('dealId');
  //   }

  //   return config;
  // });


  let redirected = false;

  /* -------- Preview routing -------- */
  // if (offer.value.isPreview && route.name === 'Login') {
  //   let { previewType, previewTypeId } = offer.value;

  //   if (previewType === 'product' && urlParams.get('submissionVenueId')) {
  //     previewType = 'venue';
  //     previewTypeId = Number(urlParams.get('submissionVenueId'));
  //   }

  //   const targetRoute = {
  //     name: pascalCase(previewType),
  //     params: { [`${previewType}Id`]: previewTypeId },
  //     query: { t: window.token }
  //   };

  //   if (
  //     offer.value.previewDealId &&
  //     ['product', 'venue'].includes(previewType)
  //   ) {
  //     targetRoute.name = `Deal${targetRoute.name}`;
  //     targetRoute.params.dealId = offer.value.previewDealId;
  //   }

  //   await router.push(targetRoute);
  //   redirected = true;
  // }

  /* -------- Normal auth routing -------- */
  // else if (!offer.value.isUnregisteredLink) {
  //   if (route.name === 'Login' && isLoggedIn.value) {
  //     loading.value = false;
  //     await router.push({ name: 'Experiences', query: { t: window.token } });
  //     redirected = true;
  //   }

  //   if (route.name !== 'Login' && !isLoggedIn.value) {
  //     await router.push({ name: 'Login', query: { t: window.token } });
  //     redirected = true;
  //   }
  // }

  if (!redirected) gtmPageView(route);

  loading.value = false;
  initialFetch.value = true;
});

// onUnmounted(() => {
//   window.removeEventListener('bw-intercom-show', onIntercomMessengerShow);
//   window.removeEventListener('bw-intercom-hide', onIntercomMessengerHide);
// });

const headInitialized = ref(false)

const initAppHead = () => {
  if (headInitialized.value) return

  const offer = store.state.offer
  const partner = store.state.partner
  if (!offer?.id) return

  headInitialized.value = true

  /* ===============================
     Web Manifest (Add to Homescreen)
  ================================ */
  const webManifest = document.createElement('link')
  webManifest.setAttribute('rel', 'manifest')
  webManifest.setAttribute(
    'href',
    `${import.meta.env.BASE_URL}manifest?o=${offer.id}&t=${window.token}`
  )
  document.head.appendChild(webManifest)

  /* ===============================
     Bablic Script
  ================================ */
  if (offer.bablic_snippet) {
    const snippet = offer.bablic_snippet.trim()
    const bablicScript = document.createElement('script')
    if (snippet.includes('-')) {
      bablicScript.setAttribute('referrerpolicy', 'no-referrer-when-downgrade')
      bablicScript.setAttribute('type', 'text/javascript')
      bablicScript.setAttribute('src', 'https://www.onelink-edge.com/moxie.min.js')
      bablicScript.setAttribute('data-oljs', snippet)
    } else {
      bablicScript.src = `//d.bablic.com/snippet/${snippet}.js?version=3.9`
      bablicScript.async = true
    }
    document.head.appendChild(bablicScript)
  }

  /* ===============================
     Mobile Web App Title
  ================================ */
  const appTitle = offer.name || partner?.name
  if (appTitle) {
    const meta = document.createElement('meta')
    meta.setAttribute('name', 'apple-mobile-web-app-title')
    meta.setAttribute('content', appTitle)
    document.head.appendChild(meta)

    if (/(iPad|iPhone|iPod|Android)/i.test(navigator.userAgent)) {
      document.title = appTitle
    }
  }

  /* ===============================
     Head Icons
  ================================ */
  if (offer.media) {
    offer.media
      .filter(m =>
        ['Favicon', 'Icon152', 'Icon196'].includes(m.location)
      )
      .forEach(m => {
        const link = document.querySelector(`#${m.location}`)
        if (link) link.setAttribute('href', m.url)
      })
  }

  /* ===============================
     iOS Splash Screens
  ================================ */
  if (partner?.media) {
    const base =
      '//res.cloudinary.com/bandwango/image/upload/'
    const quality = 'q_auto/'

    const splashLinks = document.querySelectorAll(
      '[rel="apple-touch-startup-image"]'
    )

    const logo = partner.media.find(m => m.location === 'Logo')
    if (!logo) return

    splashLinks.forEach(sc => {
      const dims = sc.dataset.sizes.split(', ')
      const size = `w_${dims[0] * dims[2]},h_${dims[1] * dims[2]},c_lpad/`
      const logoSize = `w_${dims[0] / 1.5},c_scale/`
      const img = logo.url.slice(logo.url.indexOf('partners'))

      sc.setAttribute(
        'href',
        `${base}${logoSize}${size}${quality}${img}`
      )
    })
  }
}

/* -------------------------------------------------
   Watchers
------------------------------------------------- */
watch(
  () => route.name,
  (_, oldName) => {
    lastRouteName.value = oldName || '';
  },
  { immediate: true }
);

watch(
  () => store.state.offer?.id,
  id => {
    if (!id || headInitialized.value) return;
    initAppHead();
  },
  { immediate: true }
);
</script>

<style lang="scss">
/* App bar */
.v-app-bar .v-toolbar__content {
  border-bottom: solid 1px rgba(var(--v-theme-on-surface), 0.12) !important;
}

/* BrowserSync */
#__bs_notify__ {
  display: none !important;
  position: fixed;
  top: calc(100% - 50px) !important;
}

/* Route transitions (Vue 3) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* UI helpers */
.current-points {
  border-left: dashed 1px rgba(0, 0, 0, 0.15);
  line-height: 2rem;
  padding-left: 1rem;
}

.glow {
  background-image: radial-gradient(
    circle closest-side,
    rgb(var(--v-theme-warning)),
    transparent
  );
}

/* Intercom above Vuetify fixed layout (Safari iOS can mis-order composited fixed layers). */
#intercom-container {
  z-index: 2147483000 !important;
}

/*
 * iOS Safari "Close support chat" fallback:
.bw-intercom-close-fallback {
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(96px + env(safe-area-inset-bottom, 0px));
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: 2147483646;
}

.bw-intercom-close-fallback__btn {
  pointer-events: auto;
  appearance: none;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 9999px;
  padding: 10px 20px;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  font-family: inherit;
  color: rgb(var(--v-theme-primary, 25 118 210));
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.bw-intercom-close-fallback__btn:active {
  opacity: 0.92;
}
*/

/* Intercom above Vuetify fixed layout (Safari iOS can mis-order composited fixed layers). */
#intercom-container {
  z-index: 2147483000 !important;
}

/*
 * iOS Safari "Close support chat" fallback:
.bw-intercom-close-fallback {
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(96px + env(safe-area-inset-bottom, 0px));
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: 2147483646;
}

.bw-intercom-close-fallback__btn {
  pointer-events: auto;
  appearance: none;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 9999px;
  padding: 10px 20px;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  font-family: inherit;
  color: rgb(var(--v-theme-primary, 25 118 210));
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.bw-intercom-close-fallback__btn:active {
  opacity: 0.92;
}
*/

.progress-header-chip {
  position: relative;
  top: 0px;
  background: rgba(var(--v-theme-primary), 0.1);
  border-radius: 2rem;
  padding: 0px 3px;
  font-size: 12px;
  font-weight: 600;
  color: var(--v-theme-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 1rem;
}

.progress-header-chip--tight {
  margin-right: 0rem;
}

@media (max-width: 340px) {
  .progress-header-chip {
    margin-right: 0 !important;
  }
}
</style>
<style lang="scss" scoped>

.v-dialog {
    width: min(100dvw - 2rem, var(--project-max-width) - 2rem);
    width: 100%;
}

.show-modal-popup {  
  &__card {
    max-width: min(100dvw - 2rem, var(--project-max-width) - 2rem);
    max-height: 94%;
    margin: 0;
    border-radius: 1rem !important;
    animation: glow-pulse 2s ease-in-out infinite;
  }
}

:deep(.v-overlay__content) {  
  max-height: calc(100vh - 48px);
  max-width: min(480px, calc(100vw - 48px));
}
</style>
